import {
  Breadcrumbs,
  Divider,
  DotStatus,
  Link,
  Skeleton,
  Stack,
  Typography,
} from '@cmg/design-system';
import isEmpty from 'lodash/isEmpty';
import type { ReactNode } from 'react';

import { RolodexRecordStatus } from '../../../../../graphql';
import { recordStatusLabels } from '../../../../../types/domain/record-status/constants';
import { HighlightLabelValue } from './HighlightsLabelValue';

type BreadcrumbsLinks = {
  href?: string;
  label: string;
};

type MetaInfoLabels = {
  label: string;
  value: string;
};

type LocalHighlightsProps = Readonly<{
  breadcrumbs: BreadcrumbsLinks[];
  title: string | undefined | null;
  status?: RolodexRecordStatus;
  metaInfoLabels?: MetaInfoLabels[];
  actions: readonly ReactNode[];
  errors?: ReactNode;
}>;

const STATUS_COLOR_MAP = {
  [RolodexRecordStatus.Effective]: 'success',
  [RolodexRecordStatus.Deleted]: 'error',
} as const;

export function LocalHighlights({
  title,
  status,
  actions,
  breadcrumbs,
  metaInfoLabels,
  errors,
}: LocalHighlightsProps) {
  return (
    <Stack direction="column" gap={2} px={4} py={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
        <Stack direction="column" spacing={0.5}>
          <Breadcrumbs>
            {breadcrumbs.map(({ href, label }, index) => (
              <Link
                key={`${label}-${index}`}
                typography="caption"
                href={href}
                color={index === breadcrumbs.length - 1 ? 'text.primary' : 'inherit'}
              >
                {label}
              </Link>
            ))}
          </Breadcrumbs>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h2">{title}</Typography>
            {status && (
              <DotStatus label={recordStatusLabels[status]} color={STATUS_COLOR_MAP[status]} />
            )}
          </Stack>
          {!isEmpty(metaInfoLabels) && (
            <Stack direction="row" spacing={1} alignItems="center">
              {metaInfoLabels?.map(({ label, value }, index) => (
                <HighlightLabelValue key={`${label}-${index}`} label={label} value={value} />
              ))}
            </Stack>
          )}
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          {actions}
        </Stack>
      </Stack>
      <Divider />
      {errors}
    </Stack>
  );
}

export function LocalHighlightsSkeleton() {
  return (
    <Stack
      direction="column"
      gap={2}
      px={4}
      py={2}
      role="progressbar"
      aria-label="Loading highlights"
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
        <Stack direction="column" spacing={0.5}>
          <Breadcrumbs>
            <Typography variant="caption">
              <Skeleton variant="rounded" width={100} aria-label="Loading breadcrumb root" />
            </Typography>
            <Typography variant="caption">
              <Skeleton variant="rounded" width={100} aria-label="Loading breadcrumb item" />
            </Typography>
          </Breadcrumbs>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h2">
              <Skeleton variant="rounded" width={200} aria-label="Loading title" />
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} pt={1} alignItems="center">
            <Typography variant="overline">
              <Skeleton variant="rounded" width={300} aria-label="Loading meta info created" />
            </Typography>
            <Typography variant="overline">
              <Skeleton variant="rounded" width={300} aria-label="Loading meta info modified" />
            </Typography>
          </Stack>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Skeleton variant="rounded" width={100} height={36} aria-label="Loading actions" />
        </Stack>
      </Stack>
      <Divider />
    </Stack>
  );
}
